import { useState, useMemo } from 'react';
import { Search, ArrowUpDown, ChevronDown } from 'lucide-react';
import { parseDisplayName, formatDate } from '@/lib/utils';
import type { Lead } from '@/types/leads';
import styles from './LeadsList.module.css';

interface LeadsListProps {
  leads: Lead[];
  onSelect: (lead: Lead) => void;
}

type SortConfig = {
  key: 'name' | 'createdAt' | 'persons';
  direction: 'asc' | 'desc';
};

export const LeadsList = ({ leads, onSelect }: LeadsListProps) => {
  const [displayCount, setDisplayCount] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });

  const filteredAndSortedLeads = useMemo(() => {
    let result = [...leads];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((lead) => {
        const name = (parseDisplayName(lead.displayName)?.text || 'Unnamed Place').toLowerCase();
        const address = (lead.formattedAddress || '').toLowerCase();
        return name.includes(query) || address.includes(query);
      });
    }

    result.sort((a, b) => {
      if (sort.key === 'name') {
        const valA = parseDisplayName(a.displayName)?.text || 'Unnamed Place';
        const valB = parseDisplayName(b.displayName)?.text || 'Unnamed Place';
        return sort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      if (sort.key === 'persons') {
        const valA = a.persons.length;
        const valB = b.persons.length;
        return sort.direction === 'asc' ? valA - valB : valB - valA;
      }

      const valA = new Date(a.createdAt).getTime();
      const valB = new Date(b.createdAt).getTime();
      return sort.direction === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [leads, searchQuery, sort]);

  const visibleLeads = filteredAndSortedLeads.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedLeads.length;

  const handleSort = (key: SortConfig['key']) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchWrapper}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search businesses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableWrapper}>
        <div className={styles.scrollArea}>
          <div className={styles.scrollX}>
            <div className={styles.minWidth}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.headerRow}>
                    <th className={styles.headerCell} onClick={() => handleSort('name')}>
                      <div className={styles.headerContent}>
                        Name
                        <ArrowUpDown className={styles.sortIcon} />
                      </div>
                    </th>
                    <th className={styles.headerCell} onClick={() => handleSort('persons')}>
                      <div className={styles.headerContent}>
                        Contacts
                        <ArrowUpDown className={styles.sortIcon} />
                      </div>
                    </th>
                    <th
                      className={`${styles.headerCell} ${styles.headerCellRight}`}
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className={`${styles.headerContent} ${styles.headerContentRight}`}>
                        Added
                        <ArrowUpDown className={styles.sortIcon} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((lead) => {
                    const name = parseDisplayName(lead.displayName)?.text || 'Unnamed Place';
                    return (
                      <tr key={lead.id} onClick={() => onSelect(lead)} className={styles.row}>
                        <td className={styles.nameCell}>
                          <div className={styles.nameWrapper}>
                            <span className={styles.businessName}>{name}</span>
                            <span className={styles.address}>{lead.formattedAddress}</span>
                          </div>
                        </td>
                        <td className={styles.contactsCell}>
                          <span
                            className={
                              lead.persons.length > 0 ? styles.badgeEmerald : styles.badgeZinc
                            }
                          >
                            {lead.persons.length}
                          </span>
                        </td>
                        <td className={styles.dateCell}>{formatDate(lead.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {hasMore && (
            <div className={styles.loadMoreWrapper}>
              <button
                onClick={() => setDisplayCount((prev) => prev + 5)}
                className={styles.loadMoreButton}
              >
                <ChevronDown className={styles.loadMoreIcon} />
                Load more
              </button>
            </div>
          )}

          {filteredAndSortedLeads.length === 0 && (
            <div className={styles.emptyState}>
              <Search className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>No leads found</p>
              <p className={styles.emptyDescription}>Try adjusting your search</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
